import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  deriveScheduleSummariesById,
  type ScheduleListSummary,
  type ScheduleSummaryLabels,
} from '../components/ScheduleList/scheduleListSummary'
import type { Schedule, ScheduleEvent, ScheduleId } from '../types'

export interface UseScheduleListSummariesOptions {
  schedules: Schedule[]
  labels: ScheduleSummaryLabels
  getEventsQueryKey: (scheduleId: ScheduleId) => readonly unknown[]
  fetchScheduleEvents: (scheduleId: ScheduleId) => Promise<ScheduleEvent[]>
  staleTime?: number
  retry?: boolean
}

export interface UseScheduleListSummariesResult {
  scheduleSummariesById: Map<ScheduleId, ScheduleListSummary>
  loadingScheduleIds: ReadonlySet<ScheduleId>
}

/**
 * Shared list-level summary derivation for schedule cards.
 * Keeps app pages focused on routing/toasts/i18n wiring.
 */
export const useScheduleListSummaries = ({
  schedules,
  labels,
  getEventsQueryKey,
  fetchScheduleEvents,
  staleTime = 30000,
  retry = false,
}: UseScheduleListSummariesOptions): UseScheduleListSummariesResult => {
  const scheduleEventsQueries = useQueries({
    queries: schedules.map((schedule) => ({
      queryKey: getEventsQueryKey(schedule.id),
      queryFn: () => fetchScheduleEvents(schedule.id),
      enabled: schedules.length > 0,
      retry,
      staleTime,
    })),
  })

  const scheduleSummariesById = useMemo(
    () =>
      deriveScheduleSummariesById(
        schedules,
        (_schedule, index) => scheduleEventsQueries[index]?.data,
        labels,
      ),
    [labels, scheduleEventsQueries, schedules],
  )

  const loadingScheduleIds = useMemo(() => {
    const loadingIds = new Set<ScheduleId>()

    schedules.forEach((schedule, index) => {
      const query = scheduleEventsQueries[index]
      if (query?.isPending && query.data === undefined) {
        loadingIds.add(schedule.id)
      }
    })

    return loadingIds
  }, [scheduleEventsQueries, schedules])

  return { scheduleSummariesById, loadingScheduleIds }
}
